import { useEffect, useState } from "react";
import { Search as SearchIcon, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getMeta, searchLaw } from "@/lib/api";

export default function Search() {
    const [meta, setMeta] = useState(null);
    const [q, setQ] = useState("");
    const [caseType, setCaseType] = useState("");
    const [jurisdiction, setJurisdiction] = useState("");
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getMeta().then(setMeta).catch(() => {});
        run("");
    }, []);

    const run = async (query) => {
        setLoading(true);
        try {
            const params = {};
            if (caseType) params.case_type = caseType;
            if (jurisdiction) params.jurisdiction = jurisdiction;
            const r = await searchLaw(query, params);
            setResults(r.cases || []);
            setTotal(r.total_found || 0);
        } finally {
            setLoading(false);
        }
    };

    const submit = (e) => {
        e.preventDefault();
        run(q);
    };

    const caseTypeOptions = meta?.case_types
        ? Object.entries(meta.case_types).flatMap(([_, items]) => items)
        : [];

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                    SEARCH LAW
                </div>
                <h1 className="font-anton text-5xl sm:text-7xl uppercase leading-none mb-3">
                    6.5M+ <span className="text-[#FF3B30]">precedents</span>.
                </h1>
                <p className="font-sans text-white/60 max-w-2xl mb-8">
                    Search landmark and recent decisions. Filter by case type and jurisdiction. Free. No account.
                </p>

                <form onSubmit={submit} className="border-2 border-white/20 mb-8" data-testid="search-form">
                    <div className="grid grid-cols-1 lg:grid-cols-12 border-b-2 border-white/20">
                        <div className="lg:col-span-7 relative">
                            <SearchIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="qualified immunity, Brady, ECHR Article 3..."
                                className="w-full bg-transparent pl-12 pr-4 py-4 font-sans text-white placeholder:text-white/30 focus:outline-none"
                                data-testid="search-input"
                            />
                        </div>
                        <select
                            value={caseType}
                            onChange={(e) => setCaseType(e.target.value)}
                            className="lg:col-span-3 bg-[#111] px-4 py-4 font-mono text-xs uppercase border-l-2 border-white/20 focus:outline-none"
                            data-testid="search-case-type"
                        >
                            <option value="">ALL CASE TYPES</option>
                            {caseTypeOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                        <select
                            value={jurisdiction}
                            onChange={(e) => setJurisdiction(e.target.value)}
                            className="lg:col-span-2 bg-[#111] px-4 py-4 font-mono text-xs uppercase border-l-2 border-white/20 focus:outline-none"
                            data-testid="search-jurisdiction"
                        >
                            <option value="">ALL JURISDICTIONS</option>
                            {(meta?.jurisdictions || []).map((j) => (
                                <option key={j.value} value={j.value}>
                                    {j.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex justify-between items-center p-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                            {loading ? "SEARCHING..." : `${total} RESULTS`}
                        </div>
                        <button type="submit" className="btn-brutal btn-brutal-red" data-testid="search-submit">
                            SEARCH
                        </button>
                    </div>
                </form>

                <div className="border-2 border-white/20" data-testid="search-results">
                    {loading && (
                        <div className="p-12 text-center">
                            <Loader2 size={24} className="animate-spin mx-auto text-[#FF3B30]" />
                        </div>
                    )}
                    {!loading && results.length === 0 && (
                        <div className="p-16 text-center">
                            <div className="font-anton text-3xl uppercase mb-2">No matches.</div>
                            <div className="font-mono text-xs text-white/50 uppercase tracking-wider">
                                Try a different keyword or remove filters.
                            </div>
                        </div>
                    )}
                    {results.map((r, i) => (
                        <div
                            key={`${r.citation}-${i}`}
                            className="p-6 border-white/20 [&:not(:last-child)]:border-b-2 hover:bg-white/5 transition-colors"
                            data-testid={`result-${i}`}
                        >
                            <div className="flex items-baseline justify-between gap-4 flex-wrap">
                                <div>
                                    <div className="font-anton text-2xl uppercase leading-tight">
                                        {r.title}
                                    </div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">
                                        {r.citation} • {r.year} • {r.jurisdiction}
                                    </div>
                                </div>
                                <div className="font-mono text-xs uppercase tracking-wider px-2 py-1 border-2 border-white/40">
                                    {r.relevance_score}% MATCH
                                </div>
                            </div>
                            <p className="font-sans text-sm text-white/80 mt-3 leading-relaxed">{r.summary}</p>
                        </div>
                    ))}
                </div>
            </div>
            <Footer disclaimer={meta?.disclaimer || ""} />
        </div>
    );
}
